import { Avatar, AvatarInitials } from "@/components/Avatar";
import { Button } from "@/components/flexkit/Button";
import { SearchInput } from "@/components/flexkit/SearchInput";
import { Checkbox } from "@/components/flexkit/checkbox/Checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/podkit/popover/Popover";
import { Text } from "@/components/podkit/typography/Text";
import { cn } from "@/components/podkit/lib/cn";
import type { PlainGroup } from "@/queries/group-queries";
import { ChevronDown } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";

type GroupSelectorProps = {
    groups: PlainGroup[];
    loading?: boolean;
    onAdd: (groupIds: string[]) => void;
    className?: string;
};

export const GroupSelector: FC<GroupSelectorProps> = ({ groups, loading = false, onAdd, className }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [searchQuery, setSearchQuery] = useState("");

    // Reset selections and search when dropdown closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedIds(new Set());
            setSearchQuery("");
        }
    }, [isOpen]);

    // Filter groups by search query
    const filteredGroups = useMemo(() => {
        if (!searchQuery.trim()) return groups;
        const query = searchQuery.toLowerCase();
        return groups.filter((group) => group.name.toLowerCase().includes(query));
    }, [groups, searchQuery]);

    const handleToggleSelection = useCallback((groupId: string) => {
        // Toggle selection when clicking row or checkbox
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(groupId)) {
                next.delete(groupId);
            } else {
                next.add(groupId);
            }
            return next;
        });
    }, []);

    const handleConfirm = useCallback(() => {
        if (selectedIds.size > 0) {
            onAdd(Array.from(selectedIds));
            setIsOpen(false);
        }
    }, [selectedIds, onAdd]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            // Accept on Enter if any items are selected
            if (e.key === "Enter" && selectedIds.size > 0) {
                e.preventDefault();
                handleConfirm();
            }
        },
        [selectedIds.size, handleConfirm],
    );

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen} modal={false}>
            <PopoverTrigger asChild>
                <button
                    className={cn(
                        "flex w-full items-center justify-between",
                        "rounded-lg border border-border-light bg-surface-pure px-4 py-[7px]",
                        "text-base text-content-tertiary",
                        "hover:border-border-base",
                        "focus-visible:ring-ring focus:border-border-brand focus:bg-surface-button-clear-accent focus:outline-none focus-visible:ring-1",
                        "disabled:cursor-not-allowed disabled:opacity-50",
                        className,
                    )}
                    disabled={loading}
                    data-testid="group-selector-trigger"
                >
                    <span>Select a group to add</span>
                    <ChevronDown className="size-4 text-content-primary" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="w-[var(--radix-popover-trigger-width)] p-0"
                onKeyDown={handleKeyDown}
            >
                {/* Search bar with Share button */}
                <div className="flex items-center gap-2 p-2 pb-1">
                    <SearchInput
                        wrapperClassName="flex-1"
                        placeholder="Search by name"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClear={() => setSearchQuery("")}
                        data-testid="group-selector-search"
                        data-tracking-id="group-selector-search"
                    />
                    <Button
                        onClick={handleConfirm}
                        variant="primary"
                        disabled={selectedIds.size === 0}
                        data-testid="group-selector-share-button"
                        data-tracking-id="group-selector-share"
                    >
                        Share
                    </Button>
                </div>

                {/* Groups list */}
                <div className="max-h-80 overflow-y-auto">
                    {filteredGroups.length === 0 ? (
                        <div className="px-4 py-8 text-center text-sm text-content-secondary">
                            {searchQuery.trim() ? "No groups found matching your search" : "No groups available"}
                        </div>
                    ) : (
                        <div className="">
                            {filteredGroups.map((group) => (
                                <div
                                    key={group.id}
                                    className="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-surface-02"
                                    onClick={() => handleToggleSelection(group.id)}
                                    data-testid={`group-selector-item-${group.id}`}
                                    data-tracking-id="group-selector-item"
                                >
                                    <Checkbox
                                        checked={selectedIds.has(group.id)}
                                        onCheckedChange={() => handleToggleSelection(group.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        data-testid={`group-selector-checkbox-${group.id}`}
                                        data-tracking-id="group-selector-checkbox"
                                    />
                                    <Avatar size={32} className="size-8 rounded-md">
                                        <AvatarInitials name={group.name} size={32} />
                                    </Avatar>
                                    <div className="flex min-w-0 flex-1 flex-col">
                                        <Text className="truncate text-base font-medium">{group.name}</Text>
                                        <Text className="text-sm text-content-tertiary">
                                            {group.memberCount}{" "}
                                            <span>{group.memberCount === 1 ? "member" : "members"}</span>
                                        </Text>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
};
